// apps/app/src/coachmark/useMatchCoachmarkSequence.ts
// Derives the five v1 coachmark sequence booleans from real sim state and
// the start-screen-visible signal. Lives in App.tsx so the controller is the
// single instance shared with CoachmarkHost + HelpButton.
//
// The trigger names in coachmark/content.ts (`match.started`, `unit.trained`,
// `combat.resolved`, `turn.ended`, `match.ended`) are illustrative — the
// actual sim emits `unit:combat`, `unit:moved`, `unit:deployed`,
// `unit:garrisoned`, `attack:city`, `building:started`, etc. We map those
// to booleans that the sequence driver can consume:
//
//   startScreenVisible  — first-run menu/menu-of-start-game is on screen,
//                          before the match has loaded
//   matchStarted        — sim is ready (status === 'ready') and snapshot non-null
//   firstUnitTrained    — any owned unit exists in the snapshot scene
//   firstCombatResolved — a `unit:combat` / `attack:city` event has fired
//   turnEnded           — sim.tick has advanced past the initial tick
//   matchEnded          — sim.victory !== null

import { useEffect, useRef } from 'react';
import type { SimWorld } from '../sim/useSimWorld/types.js';
import { useCoachmarkSequence } from './useCoachmarkSequence.js';
import type { CoachmarkController } from './useCoachmarks.js';

const COMBAT_EVENT_TYPES = new Set(['unit:combat', 'attack:city', 'attack:city_damaged']);

interface Args {
  readonly sim: SimWorld;
  readonly controller: CoachmarkController;
  /**
   * True while the menu screen (or any pre-match start surface) is visible.
   * Drives the `match.started` trigger for the `start-game` coachmark so it
   * can fire while the player is still on the menu — before the sim has
   * loaded. The flag is owned by App.tsx so the same controller can drive
   * the menu-visible hint and the match-visible hints in sequence.
   */
  readonly startScreenVisible: boolean;
  /**
   * Whether in-match hints may open on their own (the human seat's `hints`
   * assist: false for 'off'). Default true. The Help menu still works.
   */
  readonly autoOpen?: boolean;
}

export function useMatchCoachmarkSequence({
  sim,
  controller,
  startScreenVisible,
  autoOpen = true,
}: Args): void {
  const localPlayerIndex = 0;
  const combatSeenRef = useRef(false);

  // The `match.started` trigger fires when EITHER the menu is visible (start-
  // game hint) OR the match has loaded (in-match start-game hint re-arms if
  // the player reset). useCoachmarkSequence opens at most one hint per
  // trigger, so the start-game hint opens once on the menu and never reopens
  // — match-loaded callers fall through to the next trigger.
  const matchStarted = startScreenVisible || (sim.status === 'ready' && sim.snapshot !== null);

  // Count owned units in the snapshot. Real "unit trained" signal: a unit
  // owned by the local player appears in the scene graph.
  const ownedUnitCount = (() => {
    const scene = sim.snapshot?.scene?.children ?? [];
    let count = 0;
    for (const n of scene) {
      if (n.type !== 'unit') continue;
      if ((n.props?.ownerId as number | undefined) === localPlayerIndex) count++;
    }
    return count;
  })();
  const firstUnitTrained = ownedUnitCount > 0;

  const firstCombatResolved = combatSeenRef.current;
  // turnEnded: any tick advancement beyond the initial tick (tick 0 → 1+).
  // Only meaningful once the match has loaded — the menu has no tick.
  const turnEnded = sim.status === 'ready' && Number(sim.tick ?? 0n) > 0;
  const matchEnded = sim.victory !== null;

  // Latch `combatSeenRef` to true once we observe a combat-resolution event.
  // Mirrors the useRecentCombat pattern but stays monotonic (never resets).
  useEffect(() => {
    const list = sim.events ?? [];
    if (list.some((e) => COMBAT_EVENT_TYPES.has(e.type))) {
      combatSeenRef.current = true;
    }
  }, [sim.events]);

  useCoachmarkSequence({
    controller,
    matchStarted: startScreenVisible || (autoOpen && matchStarted),
    firstUnitTrained: autoOpen && firstUnitTrained,
    firstCombatResolved: autoOpen && firstCombatResolved,
    turnEnded: autoOpen && turnEnded,
    matchEnded: autoOpen && matchEnded,
  });
}
